/**
 * 后端 qnaires 契约与表单 / 预览之间的适配（structure ↔ 本地 QuestionnaireSchema）
 */
import { numberToLetterId } from './utils';
import type {
  Question,
  QuestionnaireFormData,
  QuestionnaireSchema,
  QuestionnaireSettings,
  QuestionnaireType,
  Section,
  SingleChoiceQuestion,
  MultipleChoiceQuestion,
} from './types';

/** 与后端 `scene` 一致 */
export type QnaireScene = 'survey' | 'evaluation' | 'quiz' | 'vote';

export function formTypeToScene(t: QuestionnaireType): QnaireScene {
  if (t === 'exam') return 'quiz';
  if (t === 'assessment') return 'evaluation';
  if (t === 'evaluation') return 'evaluation';
  return t as QnaireScene;
}

export function sceneToFormType(s: string | null | undefined): QuestionnaireType {
  if (s === 'evaluation') return 'evaluation';
  if (s === 'quiz') return 'quiz';
  if (s === 'vote') return 'vote';
  return 'survey';
}

/** 详情接口 structure 中单题（与后端渲染节点对齐的宽松类型） */
export type DetailItemNode = {
  id?: string | null;
  parentId?: string | null;
  content?: string;
  type?: string;
  sort?: number;
  options?: Array<{ id?: string; content?: string; sort?: number }>;
  children?: DetailItemNode[];
};

export type DetailChapter = {
  id?: string;
  name?: string;
  sort?: number;
  description?: string | null;
  items?: DetailItemNode[];
};

export type DetailStructure = {
  chapters?: DetailChapter[];
  ungroupedItems?: DetailItemNode[];
};

function mapDetailItemToQuestion(node: DetailItemNode, index: number): Question | null {
  const t = node.type;
  if (t !== 'radio' && t !== 'checkbox') {
    return null;
  }
  const base = {
    id: node.id != null && String(node.id).trim() !== '' ? String(node.id) : String(index + 1),
    title: node.content?.trim() || `第 ${index + 1} 题`,
    description: undefined,
    required: true,
    sort: node.sort,
  };
  const opts = (node.options ?? []).map((o, i) => ({
    id: o.id != null && String(o.id).trim() !== '' ? String(o.id) : numberToLetterId(i + 1),
    label: o.content?.trim() || `选项 ${i + 1}`,
    value: o.content ?? String(i),
  }));
  if (t === 'radio') {
    return { ...base, type: 'single_choice', options: opts } as SingleChoiceQuestion;
  }
  return { ...base, type: 'multiple_choice', options: opts } as MultipleChoiceQuestion;
}

function walkItems(nodes: DetailItemNode[] | undefined, out: Question[], startIdx: { n: number }) {
  if (!nodes?.length) return;
  for (const node of nodes) {
    const q = mapDetailItemToQuestion(node, startIdx.n);
    if (q) {
      out.push(q);
      startIdx.n += 1;
    }
    if (node.children?.length) {
      walkItems(node.children, out, startIdx);
    }
  }
}

/** 将 `get({ withAll: true }).structure` 转为 QuizRenderer 可用的 QuestionnaireSchema（仅选择题） */
export function detailStructureToQuestionnaireSchema(
  structure: DetailStructure | null | undefined,
  settings: QuestionnaireSettings
): QuestionnaireSchema {
  const sections: Array<Section | Question> = [];
  const chapters = [...(structure?.chapters ?? [])].sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  let idx = { n: 0 };

  for (const ch of chapters) {
    const bucket: Question[] = [];
    walkItems(ch.items, bucket, idx);
    if (bucket.length > 0 || ch.name) {
      sections.push({
        type: 'section',
        id: ch.id || ch.name || `s${sections.length}`,
        name: ch.name || '',
        description: ch.description ?? undefined,
        questions: bucket,
      });
    }
  }

  const ungrouped: Question[] = [];
  walkItems(structure?.ungroupedItems, ungrouped, idx);
  for (const q of ungrouped) {
    sections.push(q);
  }

  return {
    version: '1.0',
    settings,
    sections,
  };
}

function makeChapterKey(i: number) {
  return `ch${i}`;
}
function makeItemKey(prefix: string, i: number) {
  return `${prefix}-q${i}`;
}

/** 表单 → 创建问卷 unified 的 structure（仅 radio/checkbox） */
export function formDataToCreateStructure(fd: QuestionnaireFormData) {
  if (fd.useSections) {
    const chapters = fd.sections.map((s, si) => ({
      key: makeChapterKey(si),
      name: s.name || `章节 ${si + 1}`,
      sort: si,
      description: s.description ?? null,
      items: s.questions.map((q, qi) => formQuestionToCreateNode(q, makeItemKey(`ch${si}`, qi))),
    }));
    return { chapters, ungroupedItems: [] as ReturnType<typeof formQuestionToCreateNode>[] };
  }
  const items = fd.questions.map((q, qi) => formQuestionToCreateNode(q, makeItemKey('ug', qi)));
  return { chapters: [], ungroupedItems: items };
}

function formQuestionToCreateNode(q: Question, key: string) {
  if (q.type !== 'single_choice' && q.type !== 'multiple_choice') {
    throw new Error(`当前仅支持选择题，题目 ${key} 类型为 ${q.type}`);
  }
  const type = q.type === 'single_choice' ? 'radio' : 'checkbox';
  const opts = (q as SingleChoiceQuestion | MultipleChoiceQuestion).options ?? [];
  return {
    key,
    content: q.title || '',
    type,
    sort: Number(q.order ?? 0),
    isRequired: q.required ? 1 : 0,
    options: opts.map((o, i) => ({
      content: o.label || o.value || `选项 ${i + 1}`,
      sort: i,
      optionValue: o.value != null ? String(o.value) : null,
    })),
  };
}
